import { useEffect, useMemo, useRef, useState } from "react";
import { codeChoices, codeFieldSpec, extraAdditiveCodes, filterCodeChoices, type CodeChoice } from "./codePicks";
import type { CodeFieldKey, CodePicks, ResinContext } from "./types";

interface CodePickerProps {
  id: string;
  choices: CodeChoice[];
  selectedValue: string;
  anyLabel: string;
  emptyLabel: string;
  countNoun: string;
  filterPlaceholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

function optionClassName(isSelected: boolean): string {
  return `w-full text-left bg-transparent border rounded px-2.5 py-1.5 text-[12.5px] text-rs-off-white hover:border-rs-jet-light ${
    isSelected ? "border-rs-green/40 bg-rs-green/10" : "border-transparent"
  }`;
}

export function CodePicker({
  id,
  choices,
  selectedValue,
  anyLabel,
  emptyLabel,
  countNoun,
  filterPlaceholder,
  disabled,
  onChange,
}: CodePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);

  const choiceCount = choices.length;
  const selectedChoice = choices.find((choice) => choice.value === selectedValue);
  const matchingChoices = useMemo(() => filterCodeChoices(choices, filterText), [choices, filterText]);
  const isFiltering = filterText.trim() !== "";

  useEffect(() => {
    if (!isOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFilterText("");
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen]);

  const closePicker = () => {
    setIsOpen(false);
    setFilterText("");
  };

  const pickChoice = (value: string) => {
    onChange(value);
    closePicker();
  };

  return (
    <div
      className="relative"
      ref={pickerRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && isOpen) {
          event.stopPropagation();
          closePicker();
        }
      }}
    >
      <button
        id={id}
        type="button"
        className="ctl relative flex items-center gap-2 text-left pr-10! border-rs-green/50! cursor-pointer disabled:cursor-not-allowed disabled:text-[#6e7568] disabled:border-rs-jet!"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled || choiceCount === 0}
        onClick={() => (isOpen ? closePicker() : setIsOpen(true))}
      >
        {selectedChoice ? (
          <>
            <code className="text-rs-green whitespace-nowrap">{selectedChoice.code}</code>
            <span className="truncate">{selectedChoice.readsAs}</span>
          </>
        ) : (
          <span className="truncate text-rs-light-gray">{choiceCount ? anyLabel : emptyLabel}</span>
        )}
        <span className="material-symbols-sharp absolute right-2.5 text-rs-light-gray pointer-events-none">
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-30 w-full min-w-[340px] max-w-[calc(100vw-32px)] flex flex-col gap-2 p-2.5 bg-rs-black border border-rs-jet-light rounded-md shadow-lg">
          <input
            className="ctl text-[13px] py-1.5"
            aria-label={`Filter ${countNoun}s`}
            placeholder={filterPlaceholder}
            autoComplete="off"
            autoFocus
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
          />
          <span className="text-rs-light-gray text-xs">
            {isFiltering
              ? `${matchingChoices.length} of ${choiceCount} match`
              : `${choiceCount} ${countNoun}${choiceCount === 1 ? "" : "s"}`}
          </span>
          <div className="flex flex-col gap-1 max-h-[240px] overflow-y-auto" role="listbox" aria-label={`${countNoun}s`}>
            {!isFiltering && (
              <button
                type="button"
                role="option"
                aria-selected={!selectedChoice}
                className={optionClassName(!selectedChoice)}
                onClick={() => pickChoice("")}
              >
                <span className="text-rs-light-gray">{anyLabel}</span>
              </button>
            )}
            {matchingChoices.map(({ choice, matchedGrades }) => {
              const isSelected = choice.value === selectedChoice?.value;
              return (
                <button
                  key={choice.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={optionClassName(isSelected)}
                  onClick={() => pickChoice(choice.value)}
                >
                  <span className="flex items-baseline justify-between gap-3">
                    <code className="text-rs-green">{choice.code}</code>
                    <span className="text-rs-light-gray text-xs whitespace-nowrap">{choice.meta}</span>
                  </span>
                  <span className="block">{choice.readsAs}</span>
                  {matchedGrades.length > 0 && (
                    <span className="block text-rs-light-gray text-xs">
                      Matches {matchedGrades.map((grade) => grade.label).join("; ")}
                    </span>
                  )}
                </button>
              );
            })}
            {isFiltering && matchingChoices.length === 0 && (
              <p className="m-0 text-rs-light-gray text-[12.5px]">Nothing matches "{filterText.trim()}".</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface CodeFieldProps {
  id: string;
  fieldKey: CodeFieldKey;
  resin: ResinContext;
  picks: Partial<CodePicks>;
  disabled: boolean;
  onChange: (fieldKey: CodeFieldKey, value: string) => void;
}

export function CodeField({ id, fieldKey, resin, picks, disabled, onChange }: CodeFieldProps) {
  const spec = codeFieldSpec(fieldKey);
  const { resinFamilyCode } = resin;
  const choices = useMemo(() => codeChoices(resin, spec), [resin, spec]);
  const extraAdditives = fieldKey === "additiveCodeId" ? extraAdditiveCodes(resin, picks.designationId ?? "") : [];
  const labelHint =
    fieldKey === "designationId"
      ? `The grade this entry is about. Leave it on "Any ${resinFamilyCode} resin code" when it covers the whole resin.`
      : `Pick a ${spec.label(resinFamilyCode).toLowerCase()} when the entry is about that part of the resin.`;

  return (
    <>
      <label
        className="flex items-center gap-1 mb-[7px] text-[12px] font-semibold text-rs-green"
        htmlFor={id}
        title={labelHint}
      >
        <span className="material-symbols-sharp text-[16px] leading-none">label</span>
        {spec.label(resinFamilyCode)}
      </label>
      <CodePicker
        id={id}
        choices={choices}
        selectedValue={picks[fieldKey] ?? ""}
        anyLabel={spec.anyLabel(resinFamilyCode)}
        emptyLabel={spec.emptyLabel(resinFamilyCode)}
        countNoun={spec.countNoun(resinFamilyCode)}
        filterPlaceholder={spec.filterPlaceholder}
        disabled={disabled}
        onChange={(value) => onChange(fieldKey, value)}
      />
      {extraAdditives.length > 0 && (
        <p className="hint">This resin code also has {extraAdditives.join(", ")}.</p>
      )}
    </>
  );
}
